import { useEffect, useRef } from "react";
import { useAuthStore } from "../stores/auth-store";
import { useNotificationStore } from "../stores/notification-store";
import { attemptRefresh } from "../lib/refresh-mutex";
import { queryClient } from "../lib/query-client";
import { env } from "../lib/env";
import { feedbackKeys } from "./useFeedback";
import type { Notification } from "../types/notification";
import { NotificationType } from "../types/notification";

const BASE_URL = env.API_BASE_URL;
const SSE_URL = `${BASE_URL}/notifications/stream`;

const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

export function useNotificationSSE() {
  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const backoffRef = useRef(INITIAL_BACKOFF_MS);
  const isMountedRef = useRef(true);
  // Track last known token to reconnect when it changes
  const lastTokenRef = useRef<string | null>(null);

  const connect = useRef<() => void>(null);

  useEffect(() => {
    isMountedRef.current = true;

    async function connectSSE() {
      if (!isMountedRef.current) return;

      // Cancel any existing connection
      abortControllerRef.current?.abort();
      clearTimeout(reconnectTimerRef.current);

      const { accessToken } = useAuthStore.getState();
      if (!accessToken) {
        // No token — wait for auth before connecting
        return;
      }

      lastTokenRef.current = accessToken;
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const response = await fetch(SSE_URL, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "text/event-stream",
            "Cache-Control": "no-cache",
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          if (response.status === 401) {
            // Attempt token refresh then retry
            const refreshed = await attemptRefresh();
            if (refreshed && isMountedRef.current) {
              backoffRef.current = INITIAL_BACKOFF_MS;
              connect.current?.();
            } else if (!refreshed) {
              useAuthStore.getState().clearAuth();
            }
            return;
          }
          // Non-401 error — schedule reconnect
          scheduleReconnect();
          return;
        }

        // Connection successful — reset backoff
        backoffRef.current = INITIAL_BACKOFF_MS;

        if (!response.body) {
          scheduleReconnect();
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE format: "event: <type>\ndata: <json>\n\n"
          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";

          for (const rawEvent of events) {
            if (!rawEvent.trim()) continue;

            const lines = rawEvent.split("\n");
            let eventType = "";
            let eventData = "";

            for (const line of lines) {
              if (line.startsWith("event: ")) {
                eventType = line.slice(7).trim();
              } else if (line.startsWith("data: ")) {
                eventData = line.slice(6).trim();
              }
            }

            if (eventType === "notification" && eventData) {
              try {
                const notification = JSON.parse(eventData) as Notification;
                useNotificationStore.getState().addNotification(notification);
                // Invalidate feedback queries when feedback-related notifications arrive
                if (
                  notification.type ===
                    NotificationType.FEEDBACK_COMMENT_ADDED ||
                  notification.type === NotificationType.FEEDBACK_STATUS_CHANGED
                ) {
                  void queryClient.invalidateQueries({
                    queryKey: feedbackKeys.all,
                  });
                }
              } catch {
                console.error("[SSE] Failed to parse notification:", eventData);
              }
            }
            // heartbeat events are intentionally ignored (connection health only)
          }
        }

        // Stream ended — reconnect if still mounted
        if (isMountedRef.current) {
          scheduleReconnect();
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          // Intentional abort — do not reconnect
          return;
        }
        if (isMountedRef.current) {
          scheduleReconnect();
        }
      }
    }

    function scheduleReconnect() {
      if (!isMountedRef.current) return;
      const delay = backoffRef.current;
      backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
      console.log(`[SSE] Reconnecting in ${delay}ms`);
      reconnectTimerRef.current = setTimeout(() => {
        connect.current?.();
      }, delay);
    }

    connect.current = connectSSE;
    connectSSE();

    // Reconnect on tab visibility restore if connection dropped
    function handleVisibilityChange() {
      if (!document.hidden && isMountedRef.current) {
        const controller = abortControllerRef.current;
        if (!controller || controller.signal.aborted) {
          console.log("[SSE] Tab visible — reconnecting after background drop");
          backoffRef.current = INITIAL_BACKOFF_MS;
          connect.current?.();
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Reconnect when the auth token changes (e.g., after refresh)
    const unsubscribeAuth = useAuthStore.subscribe((state) => {
      const token = state.accessToken;
      if (token && token !== lastTokenRef.current && isMountedRef.current) {
        console.log("[SSE] Token changed — reconnecting");
        backoffRef.current = INITIAL_BACKOFF_MS;
        connect.current?.();
      }
      // If token is null, stop
      if (!token && lastTokenRef.current) {
        lastTokenRef.current = null;
        abortControllerRef.current?.abort();
        clearTimeout(reconnectTimerRef.current);
      }
    });

    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
      clearTimeout(reconnectTimerRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      unsubscribeAuth();
    };
  }, []); // stable: only uses refs and store.getState()
}
